import React, { useState, useEffect, useRef } from 'react'
import { ChevronDown, Check } from 'lucide-react'
import { apiClient } from '@/lib/api'
import { useToast } from '@/hooks/use-toast'

interface Model {
  id: string
  label: string
  provider: string
  enabled: boolean
}

interface ModelSelectorProps {
  conversationId: number | null
  currentModelId: string | null
  onModelChange?: (modelId: string) => void
  disabled?: boolean
}

export function ModelSelector({ 
  conversationId, 
  currentModelId, 
  onModelChange,
  disabled = false 
}: ModelSelectorProps) {
  const [models, setModels] = useState<Model[]>([])
  const [isOpen, setIsOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [selectedModelId, setSelectedModelId] = useState<string | null>(currentModelId)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const { toast } = useToast()

  useEffect(() => {
    loadModels()
  }, [])

  useEffect(() => {
    setSelectedModelId(currentModelId)
  }, [currentModelId])

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  const loadModels = async () => {
    try {
      setIsLoading(true)
      const response = await apiClient.getAvailableModels()
      setModels(response.models.filter(m => m.enabled))
      
      // If no current model selected, use first available model
      if (!selectedModelId && response.models.length > 0) {
        const firstModel = response.models.find(m => m.enabled)
        if (firstModel) {
          setSelectedModelId(firstModel.id)
        }
      }
    } catch (error: any) {
      console.error('Failed to load models:', error)
      toast({
        title: "Error",
        description: "Failed to load available models",
        variant: "destructive",
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleModelSelect = async (modelId: string) => {
    if (modelId === selectedModelId) {
      setIsOpen(false)
      return
    }

    setSelectedModelId(modelId)
    setIsOpen(false)
    
    if (onModelChange) {
      onModelChange(modelId)
    }

    const selectedModel = models.find(m => m.id === modelId)
    toast({
      title: "Model Selected",
      description: `Selected ${selectedModel?.label || modelId}. New messages will use this model.`,
    })
  }

  const selectedModel = models.find(m => m.id === selectedModelId)

  if (isLoading) {
    return (
      <div className="px-3 py-1.5 text-xs text-gray-500 dark:text-gray-400">
        Loading models...
      </div>
    )
  }

  if (models.length === 0) {
    return null
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`
          flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium
          rounded-md border transition-all
          ${disabled
            ? 'border-gray-200 dark:border-gray-700 text-gray-400 dark:text-gray-600 cursor-not-allowed'
            : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-emerald-500 dark:hover:border-emerald-500 hover:text-emerald-600 dark:hover:text-emerald-400 bg-white dark:bg-gray-800'
          }
        `}
        title="Select model"
      >
        <span className="truncate max-w-[120px]">
          {selectedModel?.label || selectedModelId || "Select model"}
        </span>
        <ChevronDown className={`h-3 w-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && !disabled && (
        <div className="absolute bottom-full left-0 mb-2 w-56 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50 max-h-64 overflow-y-auto">
          <div className="p-1">
            {models.map((model) => (
              <button
                key={model.id}
                type="button"
                onClick={() => handleModelSelect(model.id)}
                className={`
                  w-full flex items-center justify-between px-3 py-2 text-sm rounded-md
                  transition-colors
                  ${model.id === selectedModelId
                    ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }
                `}
              >
                <span className="flex-1 text-left">{model.label}</span>
                {model.id === selectedModelId && (
                  <Check className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
